import React from 'react';
import { LinkData } from '../types';

interface Props {
  config: Record<string, any>;
  links: LinkData[];
}

export const SidebarLinkBox: React.FC<Props> = ({ config, links }) => {
  // Use correct field names from types.ts
  const {
    moduleHeading = 'Trending Posts',
    headingStyle = 'uppercase',
    linkStyle = 'text-only',
    maxLinks = 5,
    showDividers = true,
    expandable = false,
    initialVisible = 5,
    containerStyle = 'bordered',
  } = config;

  // Get heading style
  const getHeadingStyle = (): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      fontSize: '1rem',
      margin: '0 0 0.75rem 0',
      color: '#1e3a5f',
    };
    switch (headingStyle) {
      case 'bold':
        return { ...baseStyle, fontWeight: 700 };
      case 'underlined':
        return { ...baseStyle, fontWeight: 600, paddingBottom: '0.5rem', borderBottom: '2px solid #2db77a' };
      default: // uppercase
        return {
          ...baseStyle,
          fontWeight: 600,
          textTransform: 'uppercase',
          fontSize: '0.85rem',
          letterSpacing: '0.05em',
        };
    }
  };

  // Get container style
  const getContainerStyle = (): React.CSSProperties => {
    switch (containerStyle) {
      case 'flat':
        return { background: 'white' };
      case 'shadowed':
        return { background: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' };
      default: // bordered
        return { background: 'white', border: '1px solid #e5e7eb' };
    }
  };

  const displayLinks = links.slice(0, expandable ? initialVisible : maxLinks);

  return (
    <div
      style={{
        ...getContainerStyle(),
        padding: '1rem',
        borderRadius: '8px',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        maxWidth: '280px',
      }}
    >
      <h3 style={getHeadingStyle()}>{moduleHeading}</h3>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {displayLinks.map((link, i) => (
          <li
            key={i}
            style={{
              padding: '0.5rem 0',
              borderTop: showDividers && i > 0 ? '1px solid #e5e7eb' : 'none',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
            }}
          >
            {linkStyle === 'numbered' && (
              <span style={{ color: '#2db77a', fontWeight: 700, fontSize: '0.9rem', minWidth: '1.25rem' }}>
                {i + 1}
              </span>
            )}
            {linkStyle === 'icon-prefix' && <span style={{ color: '#2db77a', fontSize: '0.9rem' }}>→</span>}
            <a
              href={link.url}
              style={{ color: '#1e3a5f', textDecoration: 'none', fontSize: '0.9rem', lineHeight: 1.4 }}
            >
              {link.title || link.anchorText || link.url}
            </a>
          </li>
        ))}
      </ul>
      {expandable && links.length > (initialVisible as number) && (
        <a
          href="#"
          style={{
            display: 'inline-block',
            marginTop: '0.5rem',
            color: '#64748b',
            textDecoration: 'none',
            fontSize: '0.85rem',
          }}
        >
          Show More
        </a>
      )}
    </div>
  );
};
